import { formatDate } from './dateFormatter';

/**
 * Creates a downloadable file from content
 * @param {string} content - The content to download
 * @param {string} fileName - The name of the file
 * @param {string} contentType - The MIME type of the content
 */
export const downloadFile = (content, fileName, contentType) => {
  const blob = new Blob([content], { type: contentType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/**
 * Converts message data to CSV format
 * @param {Array} messages - Array of message objects
 * @returns {string} - CSV formatted string
 */
export const convertToCSV = (messages) => {
  const header = 'Timestamp,Sender,Message\n';
  const rows = messages.map(msg => {
    const timestamp = formatDate(msg.timestamp);
    const sender = msg.sender === 'user' ? 'You' : 'AI Assistant';
    // Escape quotes and remove new lines for CSV compatibility
    const text = `"${msg.text.replace(/"/g, '""').replace(/\n/g, ' ')}"`;
    return `${timestamp},${sender},${text}`;
  }).join('\n');
  
  return header + rows;
};

/**
 * Prepares conversation data for JSON export
 * @param {Object} conversation - Conversation object
 * @param {Array} messages - Array of message objects
 * @returns {Object} - Formatted data for JSON export
 */
export const prepareJSONExport = (conversation, messages) => {
  return {
    title: conversation.metadata?.title || 'Untitled Chat',
    createdAt: formatDate(conversation.metadata?.createdAt),
    updatedAt: formatDate(conversation.metadata?.updatedAt),
    messages: messages.map(msg => ({
      sender: msg.sender === 'user' ? 'You' : 'AI Assistant',
      timestamp: formatDate(msg.timestamp),
      text: msg.text
    }))
  };
};

/**
 * Prepares conversation data for text export
 * @param {Object} conversation - Conversation object
 * @param {Array} messages - Array of message objects
 * @returns {string} - Formatted text for export
 */
export const prepareTextExport = (conversation, messages) => {
  let text = `${conversation.metadata?.title || 'Untitled Chat'}\n`;
  text += `Created: ${formatDate(conversation.metadata?.createdAt)}\n`;
  text += `Last Updated: ${formatDate(conversation.metadata?.updatedAt)}\n\n`;
  text += '==== Conversation ====\n\n';
  
  messages.forEach(msg => {
    text += `[${formatDate(msg.timestamp)}] ${msg.sender === 'user' ? 'You' : 'AI Assistant'}:\n`;
    text += `${msg.text}\n\n`;
  });
  
  return text;
};

/**
 * Generates a filename for export based on conversation title
 * @param {Object} conversation - Conversation object
 * @returns {string} - Generated filename
 */
export const generateExportFilename = (conversation) => {
  const title = (conversation.metadata?.title || 'chat')
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]+/g, '');
  
  const timestamp = new Date().toISOString().split('T')[0];
  return `chat-${title}-${timestamp}`;
};